import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import { Spinner2, Text } from 'components';
import { CtaRoutes, GradientBackground } from './components';
import { openMobileAppLink } from 'core';
import { Api, CTA, theme } from 'lib';

/*
  Layout for the CTA flow. The containers mount it at /shoutout/:username/*
  and the steps are descendant routes: index (intro), signup, login, compose —
  with success to follow.
  The layout stays mounted across step changes, so the fetched user and the
  shout-out draft survive navigation between steps.
*/
const CTAScreenV2 = ({ ctaType }) => {
  const { username } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.user);

  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const isSendShoutout = ctaType === CTA.SEND_SHOUTOUT;
  const basePath = `/shoutout/${username}`;

  useEffect(() => {
    fetchOtherUser();

    // Social signups skip onboarding, so they have no username. Remember the
    // active flow so the Onboarding gate in User.js can send them back here
    // (e.g. via browser back onto /login) instead of showing the generic screen.
    dispatch({
      type: 'UPDATE_APP_STATE',
      payload: { ctaFlowPath: basePath }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  async function fetchOtherUser() {
    setLoading(true);

    const user = await Api.get(`/users/getByUsername/${username}`);
    if (!user) {
      setLoading(false);
      return;
    }

    setOtherUser(user);
    setData({ entity: user });
    setLoading(false);
  }

  function handleStart() {
    navigate(currentUser?._id ? `${basePath}/compose` : `${basePath}/signup`);
  }

  function handleLogin() {
    navigate(currentUser?._id ? `${basePath}/compose` : `${basePath}/login`);
  }

  // shared by the signup and login steps
  function handleAuthComplete(user) {
    // SET_USER swaps the NotUser container for User and remounts everything —
    // the step survives because it lives in the URL. navigateTo keeps User.js
    // from redirecting the fresh login to /home. A social signup is already in
    // the User container (mount effect already ran), so navigateTo would linger.
    dispatch({
      type: 'SET_USER',
      payload: currentUser?._id ? user : { ...user, navigateTo: `shoutout/${username}/compose` }
    });

    navigate(`${basePath}/compose`, { replace: true });
  }

  // success step — deep-link the new fan into the app on the team's profile;
  // if the app doesn't take over, openMobileAppLink falls back to the
  // get-the-app page (all platforms)
  function handleContinueInApp() {
    openMobileAppLink(`profile/${username}`);
  }

  // only sendShoutout is supported for now; other CTA types keep their existing flows
  if (!isSendShoutout) return null;

  if (loading)
    return (
      <GradientBackground>
        <Spinner2 col={theme.SECONDARY} />
      </GradientBackground>
    );

  if (!otherUser)
    return (
      <GradientBackground>
        <Text h3 marg="16px 0 0" col="#fff">
          {t('profile.userNotFound', { username })}
        </Text>
      </GradientBackground>
    );

  return (
    <CtaRoutes
      basePath={basePath}
      otherUser={otherUser}
      onStart={handleStart}
      onLogin={handleLogin}
      onAuthComplete={handleAuthComplete}
      onContinueInApp={handleContinueInApp}
    />
  );
};

export default CTAScreenV2;
